import clsx from 'clsx';
import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

function useDismiss(open: boolean, onDismiss: () => void) {
  // The panel is portaled out of the trigger's subtree, so "inside" means inside either box.
  const box = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  // A ref, so a handler rebuilt every keystroke does not resubscribe the document.
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;

  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!box.current?.contains(target) && !panel.current?.contains(target)) dismiss.current();
    };
    const shut = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss.current();
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', shut);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', shut);
    };
  }, [open]);

  return { box, panel };
}

/**
 * Portaled to <body>: the header's backdrop blur makes it the containing block for `fixed`
 * descendants. The trigger sits inside the dismiss box, so tapping it while open closes once.
 */
export function HeaderMenu({
  open,
  onDismiss,
  trigger,
  width = '19rem',
  className,
  children,
}: {
  open: boolean;
  onDismiss: () => void;
  trigger: ReactNode;
  width?: string;
  className?: string;
  children: ReactNode;
}) {
  const { box, panel } = useDismiss(open, onDismiss);
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const rect = box.current?.getBoundingClientRect();
      if (rect) setAnchor({ top: rect.bottom, right: window.innerWidth - rect.right });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open, box]);

  return (
    <div ref={box} className={clsx('relative', className)}>
      {trigger}
      {open &&
        anchor &&
        createPortal(
          <>
            <div
              aria-hidden
              className="menu-scrim fixed inset-0 z-40 touch-none bg-scrim sm:hidden"
            />
            <div
              ref={panel}
              // CSS variables: Tailwind only builds classes it can read in the source.
              style={
                {
                  '--menu-width': width,
                  '--menu-top': `${anchor.top}px`,
                  '--menu-right': `${anchor.right}px`,
                } as CSSProperties
              }
              className={clsx(
                'menu-in fixed z-40 flex flex-col gap-3 border-line bg-surface-raised shadow-lg',
                'scroll-quiet right-0 bottom-0 left-0 max-h-[85dvh] overflow-y-auto overscroll-contain rounded-t-sheet border-t p-4 pb-[max(1.25rem,env(safe-area-inset-bottom))]',
                'sm:top-[calc(var(--menu-top)+0.5rem)] sm:right-[var(--menu-right)] sm:bottom-auto sm:left-auto sm:max-h-[calc(100dvh-var(--menu-top)-2rem)] sm:w-[min(var(--menu-width),calc(100vw-var(--menu-right)-0.75rem))] sm:rounded-box sm:border sm:p-3.5 sm:pb-3.5',
              )}
            >
              {children}
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
